import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import TrustBadge from '../../components/TrustBadge'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { getMisSuscripciones, getMisPedidos, getDireccionPerfil, getMisFacturas } from '../../lib/portalClienteApi'
import { WA_NUMERO } from '../../lib/constantes'

const STORAGE_CARRITO = 'limpack_carrito_v1'

function leerCarrito() {
  try {
    const arr = JSON.parse(localStorage.getItem(STORAGE_CARRITO))
    return Array.isArray(arr) ? arr : []
  } catch { return [] }
}

export default function Cuenta() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [suscripciones, setSuscripciones] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [direccionPerfil, setDireccionPerfil] = useState(null)
  const [facturas, setFacturas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [carrito, setCarrito] = useState(leerCarrito)

  useEffect(() => { if (user?.id) cargar() }, [user?.id])

  // Re-leer carrito si cambia en otra pestaña / componente
  useEffect(() => {
    function onChange() { setCarrito(leerCarrito()) }
    window.addEventListener('storage', onChange)
    window.addEventListener('lp:carrito-changed', onChange)
    return () => {
      window.removeEventListener('storage', onChange)
      window.removeEventListener('lp:carrito-changed', onChange)
    }
  }, [])

  function vaciarCarrito() {
    if (!confirm('¿Vaciar el carrito? Se van a perder los productos que tenías.')) return
    localStorage.removeItem(STORAGE_CARRITO)
    window.dispatchEvent(new Event('lp:carrito-changed'))
    setCarrito([])
  }

  const carritoCantidad = carrito.reduce((s, it) => s + (it.cantidad || 0), 0)
  const carritoSubtotal = carrito.reduce((s, it) => s + (it.precio_unit || 0) * (it.cantidad || 0), 0)

  async function cargar() {
    setCargando(true)
    const [ss, peds, dp, facts] = await Promise.all([
      getMisSuscripciones(user.id),
      getMisPedidos(50),
      getDireccionPerfil(),
      getMisFacturas(user.id, 20),
    ])
    setSuscripciones(ss)
    setPedidos(peds)
    setDireccionPerfil(dp)
    setFacturas(facts)
    setCargando(false)
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const direcciones = suscripciones.length
  const totalPedidos = pedidos.length
  const susc = suscripciones[0]
  const tarjeta = susc?.mp_card_brand && susc?.mp_card_last4
    ? `${susc.mp_card_brand.toUpperCase()} ···${susc.mp_card_last4}`
    : null

  const initial = (profile?.nombre || user?.email || 'U').charAt(0).toUpperCase()
  const nombreCompleto = profile?.nombre || (user?.email || '').split('@')[0]
  const telefono = profile?.phone

  return (
    <ClienteLayout title="Mi Cuenta">
      <div className="cu-wrap">
        <div className="cu-headline">
          <h1>Mi Cuenta</h1>
          <p>Datos personales, direcciones, pago y soporte.</p>
        </div>

        <div className="cu-grid">
          {/* Perfil hero */}
          <div className="cu-perfil">
            <div className="cu-avatar">{initial}</div>
            <div className="cu-perfil-info">
              <div className="cu-nombre">{nombreCompleto}</div>
              <div className="cu-email">{user?.email}</div>
              {telefono && <div className="cu-tel">{telefono}</div>}
            </div>
            <Link to="/cliente/perfil" className="cu-edit-btn">Editar perfil</Link>
          </div>

          {/* Carrito sin terminar */}
          {carritoCantidad > 0 && (
            <div className="cu-carrito-card">
              <div className="cu-carrito-icon">🛒</div>
              <div className="cu-carrito-body">
                <div className="cu-carrito-titulo">Tu carrito sigue abierto</div>
                <div className="cu-carrito-sub">
                  Tenés {carritoCantidad} {carritoCantidad === 1 ? 'producto' : 'productos'} sin comprar
                  {' · '}<b>${carritoSubtotal.toLocaleString('es-UY')}</b>
                </div>
                <div className="cu-carrito-acciones">
                  <button className="cu-carrito-btn-primary" onClick={() => navigate('/catalogo')}>
                    Volver a la tienda
                  </button>
                  <button className="cu-carrito-btn-secondary" onClick={vaciarCarrito}>
                    Vaciar carrito
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Datos */}
          <div className="cu-col">
            <h2 className="cu-section">Datos</h2>
            <div className="cu-list">
              {/* Dirección de perfil — SIEMPRE visible (sirve para banner Vecinos sin compra).
                  Fallback: si no hay dirección de perfil cargada pero el cliente tiene 1
                  suscripción, mostrar esa dirección. Con 2+ subs muestra cantidad. */}
              <Link to="/cliente/mi-direccion" className="cu-row">
                <div className="cu-row-icon">📍</div>
                <div className="cu-row-label">Mi dirección</div>
                <div className="cu-row-meta">
                  {direccionPerfil?.direccion
                    ? `${direccionPerfil.direccion}${direccionPerfil.barrio_nombre ? ' · ' + direccionPerfil.barrio_nombre : ''}`
                    : suscripciones.length === 1 && susc?.direccion
                      ? `${susc.direccion}${(susc.barrio_obj?.nombre || susc.barrio) ? ' · ' + (susc.barrio_obj?.nombre || susc.barrio) : ''}`
                      : suscripciones.length >= 2
                        ? `${suscripciones.length} direcciones · ver kits`
                        : 'Sin cargar'}
                </div>
                <div className="cu-row-arrow">›</div>
              </Link>
              {(suscripciones.length > 0 || totalPedidos > 0) && (
                <Link to="/cliente/mis-direcciones" className="cu-row">
                  <div className="cu-row-icon">📦</div>
                  <div className="cu-row-label">
                    {suscripciones.length > 0 ? 'Mis kits y entregas' : 'Direcciones de pedidos'}
                  </div>
                  <div className="cu-row-meta">
                    {suscripciones.length > 0
                      ? `${suscripciones.length} kit${suscripciones.length === 1 ? '' : 's'}`
                      : `${totalPedidos} pedido${totalPedidos === 1 ? '' : 's'}`}
                  </div>
                  <div className="cu-row-arrow">›</div>
                </Link>
              )}
              <Link to="/cliente/pedidos" className="cu-row">
                <div className="cu-row-icon">📜</div>
                <div className="cu-row-label">Historial de pedidos</div>
                <div className="cu-row-meta">{totalPedidos} pedido{totalPedidos === 1 ? '' : 's'}</div>
                <div className="cu-row-arrow">›</div>
              </Link>
              <Link to={susc ? `/cliente/cargar-tarjeta/${susc.id}` : '#'} className="cu-row">
                <div className="cu-row-icon">💳</div>
                <div className="cu-row-label">Método de pago</div>
                <div className="cu-row-meta">{tarjeta || 'Sin tarjeta cargada'}</div>
                <div className="cu-row-arrow">›</div>
              </Link>
              <Link to="/cliente/facturacion" className="cu-row">
                <div className="cu-row-icon">🧾</div>
                <div className="cu-row-label">Datos de facturación</div>
                <div className="cu-row-meta">RUT, razón social</div>
                <div className="cu-row-arrow">›</div>
              </Link>
              <button className="cu-row" onClick={() => alert('Próximamente: gestionar notificaciones')}>
                <div className="cu-row-icon">🔔</div>
                <div className="cu-row-label">Notificaciones</div>
                <div className="cu-row-meta">Email · WhatsApp</div>
                <div className="cu-row-arrow">›</div>
              </button>
            </div>
            <div style={{ marginTop: 14 }}>
              <TrustBadge variant="default" />
            </div>
          </div>

          {/* Mis facturas — solo si tiene alguna */}
          {facturas.length > 0 && (
            <div className="cu-col">
              <h2 className="cu-section">Mis facturas</h2>
              <div className="cu-list">
                {facturas.slice(0, 5).map(f => {
                  const esNC = f.tipo_cfe === 'e_nota_credito' || f.tipo_cfe === 'eTicketNotaCredito' || f.tipo_cfe === 'eFacturaNotaCredito'
                  const fecha = f.fecha_emision
                    ? new Date(f.fecha_emision + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' })
                    : ''
                  const monto = Number(f.monto_total || 0).toLocaleString('es-UY')
                  const tipo = esNC ? 'Nota de Crédito' : (f.tipo_cfe?.includes('actura') ? 'Factura' : 'Ticket')
                  const url = f.shortlink_hash ? `https://limpack.uy/f/${f.shortlink_hash}` : null
                  return (
                    <a key={f.id}
                       href={url || '#'}
                       target={url ? '_blank' : undefined}
                       rel={url ? 'noopener noreferrer' : undefined}
                       className="cu-row"
                       style={{ opacity: url ? 1 : 0.5, cursor: url ? 'pointer' : 'default' }}>
                      <div className="cu-row-icon">{esNC ? '↩' : '🧾'}</div>
                      <div className="cu-row-label">
                        {tipo} {f.serie}-{f.numero}
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, marginTop: 2 }}>{fecha}</div>
                      </div>
                      <div className="cu-row-meta" style={{ fontWeight: 700, color: esNC ? '#dc2626' : '#0f172a' }}>
                        {esNC ? '−' : ''}${monto}
                      </div>
                      <div className="cu-row-arrow">{url ? '📄' : '⏳'}</div>
                    </a>
                  )
                })}
              </div>
              {facturas.length > 5 && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, textAlign: 'center' }}>
                  Mostrando 5 de {facturas.length} · escribinos por WhatsApp si necesitás otra
                </div>
              )}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, lineHeight: 1.5 }}>
                🔒 Estas facturas son válidas ante DGI. El link <code style={{ background:'#f1f5f9', padding:'1px 5px', borderRadius:4 }}>limpack.uy/f/…</code> es nuestro (anti-phishing).
              </div>
            </div>
          )}

          {/* Comunidad y ayuda */}
          <div className="cu-col">
            <h2 className="cu-section">Comunidad y ayuda</h2>
            <Link to="/cliente/vecinos" className="cu-vecinos">
              <div className="cu-vecinos-h">👥 Vecinos en tu cuadra</div>
              <div className="cu-vecinos-sub">Si llegan a 10, todos ganan <b>5% de descuento permanente en la suscripción</b>.</div>
              <div className="cu-vecinos-cta">Ver y compartir →</div>
            </Link>
            <a className="cu-soporte"
               href={`https://wa.me/${WA_NUMERO}?text=${encodeURIComponent(
                 `Hola Limpack! Necesito ayuda con mi cuenta.` +
                 (profile?.nombre || user?.email
                   ? `\n\n— ${profile?.nombre?.split(' ')[0] || user?.email}${profile?.nombre && user?.email ? ` (${user.email})` : ''}`
                   : '')
               )}`}
               target="_blank" rel="noreferrer">
              <div className="cu-soporte-icon">💬</div>
              <div className="cu-soporte-info">
                <div className="cu-soporte-titulo">Hablar con Limpack</div>
                <div className="cu-soporte-sub">WhatsApp · Lun a Vie 9–18hs</div>
              </div>
              <div className="cu-soporte-arrow">›</div>
            </a>
            <button className="cu-logout" onClick={handleLogout}>Salir de mi cuenta</button>

            <Link to="/cliente/dar-de-baja" className="cu-baja">Dar de baja mi cuenta</Link>

            {/* Links legales */}
            <div className="cu-legal">
              <a href="https://limpack.uy/privacidad" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>
              <span className="cu-legal-sep">·</span>
              <a href="https://limpack.uy/terminos" target="_blank" rel="noopener noreferrer">Términos y Condiciones</a>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .cu-legal {
          margin-top: 18px;
          text-align: center;
          font-size: 12px;
          color: #94a3b8;
          line-height: 1.6;
        }
        .cu-legal a {
          color: #64748b;
          text-decoration: none;
          border-bottom: 1px dotted #cbd5e1;
        }
        .cu-legal a:hover { color: #1a5cb8; border-bottom-color: #1a5cb8 }
        .cu-legal-sep { margin: 0 8px; color: #cbd5e1 }
      `}</style>

      <style>{`
        .cu-wrap { max-width: 1100px; margin: 0 auto; padding: 16px 16px 32px }
        .cu-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em }
        .cu-headline p { font-size: 14px; color: var(--cl-muted); margin-top: 4px; line-height: 1.5 }

        .cu-grid { display: grid; grid-template-columns: 1fr; gap: 18px; margin-top: 18px }

        .cu-perfil {
          grid-column: 1 / -1;
          background: linear-gradient(135deg, var(--cl-navy), var(--cl-navy-2));
          color: #fff; padding: 20px; border-radius: 18px;
          display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
        }
        .cu-avatar {
          width: 56px; height: 56px; border-radius: 50%;
          background: linear-gradient(135deg, var(--cl-teal), var(--cl-blue));
          display: flex; align-items: center; justify-content: center;
          font-size: 22px; font-weight: 900;
        }
        .cu-perfil-info { flex: 1; min-width: 0 }
        .cu-nombre { font-size: 17px; font-weight: 800 }
        .cu-email { font-size: 12px; opacity: .75; margin-top: 2px; word-break: break-all }
        .cu-tel { font-size: 11px; opacity: .65; margin-top: 1px }
        .cu-edit-btn {
          background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2);
          color: #fff; padding: 8px 14px; border-radius: 999px;
          font-size: 12px; font-weight: 700; text-decoration: none;
        }
        .cu-edit-btn:hover { background: rgba(255,255,255,.2) }

        .cu-section { font-size: 14px; font-weight: 900; color: var(--cl-text); margin: 0 0 12px; letter-spacing: -.01em }

        /* Carrito sin terminar */
        .cu-carrito-card {
          grid-column: 1 / -1;
          display: flex; gap: 14px;
          padding: 16px 18px;
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          border: 1.5px solid #fdba74;
          border-radius: 16px;
          align-items: flex-start;
        }
        .cu-carrito-icon { font-size: 28px; line-height: 1; flex-shrink: 0 }
        .cu-carrito-body { flex: 1; min-width: 0 }
        .cu-carrito-titulo { font-size: 15px; font-weight: 800; color: #9a3412 }
        .cu-carrito-sub { font-size: 13px; color: #c2410c; margin-top: 2px; line-height: 1.4 }
        .cu-carrito-sub b { color: #7c2d12 }
        .cu-carrito-acciones { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap }
        .cu-carrito-btn-primary {
          padding: 9px 16px; border: 0;
          background: #ea580c; color: #fff;
          border-radius: 10px; font-weight: 800; font-size: 13px;
          cursor: pointer; font-family: inherit;
        }
        .cu-carrito-btn-primary:hover { background: #c2410c }
        .cu-carrito-btn-secondary {
          padding: 9px 14px; border: 1px solid #fdba74;
          background: #fff; color: #9a3412;
          border-radius: 10px; font-weight: 700; font-size: 13px;
          cursor: pointer; font-family: inherit;
        }
        .cu-carrito-btn-secondary:hover { background: #fff7ed }

        .cu-list { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 14px; overflow: hidden }
        .cu-row {
          display: flex; align-items: center; gap: 14px;
          padding: 14px 16px; border-bottom: 1px solid var(--cl-line2);
          cursor: pointer; transition: background .12s;
          background: none; border-left: 0; border-right: 0; border-top: 0;
          text-decoration: none; color: var(--cl-text); width: 100%;
          font-family: inherit; text-align: left;
        }
        .cu-row:hover { background: var(--cl-line2) }
        .cu-row:last-child { border-bottom: 0 }
        .cu-row-icon { font-size: 18px; width: 22px; text-align: center; flex-shrink: 0 }
        .cu-row-label { flex: 1; font-size: 13.5px; font-weight: 700 }
        .cu-row-meta { font-size: 12px; color: var(--cl-muted); flex-shrink: 0 }
        .cu-row-arrow { color: var(--cl-muted2); font-size: 16px; flex-shrink: 0 }

        .cu-vecinos {
          padding: 18px; border-radius: 14px;
          background: linear-gradient(135deg, #ecfdf5, #d1fae5);
          border: 1px solid #86efac; text-decoration: none;
          margin-bottom: 12px; display: block;
          transition: transform .15s;
        }
        .cu-vecinos:hover { transform: translateY(-1px) }
        .cu-vecinos-h { font-size: 14px; font-weight: 800; color: #065f46 }
        .cu-vecinos-sub { font-size: 12px; color: #047857; margin-top: 6px; line-height: 1.5 }
        .cu-vecinos-cta { font-size: 12px; font-weight: 800; color: #065f46; margin-top: 10px; letter-spacing: .04em }

        .cu-soporte {
          padding: 14px; border-radius: 14px;
          background: linear-gradient(135deg, #25d366, #128c7e);
          color: #fff; display: flex; align-items: center; gap: 12px;
          text-decoration: none; margin-bottom: 12px;
          transition: transform .15s;
        }
        .cu-soporte:hover { transform: translateY(-1px) }
        .cu-soporte-icon { font-size: 22px }
        .cu-soporte-info { flex: 1 }
        .cu-soporte-titulo { font-size: 13px; font-weight: 800 }
        .cu-soporte-sub { font-size: 11px; opacity: .9; margin-top: 1px }
        .cu-soporte-arrow { font-size: 16px; opacity: .8 }

        .cu-logout {
          width: 100%; padding: 12px;
          background: var(--cl-card); border: 1px solid var(--cl-line);
          border-radius: 12px; text-align: center;
          font-size: 13px; font-weight: 800; color: #f87171;
          cursor: pointer; font-family: inherit;
          transition: border-color .15s, background .15s;
        }
        .cu-logout:hover { border-color: rgba(248,113,113,.5); background: rgba(248,113,113,.08) }

        .cu-baja {
          display: block;
          margin-top: 10px;
          text-align: center;
          font-size: 12px; font-weight: 600;
          color: var(--cl-muted);
          text-decoration: none;
          padding: 8px;
          border-radius: 8px;
          transition: color .15s;
        }
        .cu-baja:hover { color: #f87171; text-decoration: underline }

        @media (min-width: 900px) {
          .cu-wrap { padding: 36px 44px }
          .cu-headline h1 { font-size: 32px }
          .cu-grid { grid-template-columns: 1fr 1fr; gap: 24px }
          .cu-perfil { padding: 24px 26px }
          .cu-avatar { width: 64px; height: 64px; font-size: 26px }
          .cu-nombre { font-size: 19px }
          .cu-email { font-size: 13px }
          .cu-section { font-size: 16px; margin-bottom: 14px }
          .cu-row { padding: 16px 20px }
          .cu-row-label { font-size: 14px }
        }
      `}</style>
    </ClienteLayout>
  )
}
